import { useState, useEffect, ReactElement } from "react";
import { useLocation } from "_/components/router";
import { t } from "@lingui/core/macro";
import { useForm } from "react-hook-form";

import { H5, P } from "_/components/typography";
import { useAddToast } from "_/components/toasts";
import { FormTextField } from "_/components/text-field";
import { SubmitCancelButtons } from "_/components/modal";
import { Form } from "_/components/form";

import {
  useCheckUser,
  useRequestPasswordReset,
  usePasswordReset,
} from "_/data/users";

import { Uuid } from "_/types";

import { routeUrls } from "_/routes";

import * as S from "./styled";
/**
 * Expected query string recovery parameters.
 */
type RecoveryParams = {
  /**
   * Password recovery token.
   */
  t: string;
  /**
   * User account ID for which the recovery token is valid.
   */
  i: Uuid;
};

/**
 * Form component for requesting a password reset email.
 */
const RequestResetForm = (): ReactElement => {
  const addToast = useAddToast();
  const [_location, setLocation] = useLocation();
  const requestPasswordReset = useRequestPasswordReset();
  const defaultValues = { email: "" };
  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm({ defaultValues });

  const onSubmit = handleSubmit(async (data) => {
    try {
      await requestPasswordReset.mutateAsync(data);

      setLocation(routeUrls.public.login);

      addToast({
        title: t`common.success`,
        content: t`components.request-reset-form.success-content ${data.email}`,
        kind: "success",
        timeout: 10_000,
      });
    } catch {
      addToast({
        title: t`common.error`,
        content: t`components.request-reset-form.error-content`,
        kind: "danger",
      });
    }
  });

  return (
    <S.FormWrapper>
      <H5>{t`components.request-reset-form.title`}</H5>
      <P>{t`components.request-reset-form.description`} </P>
      <Form onSubmit={onSubmit}>
        <FormTextField
          label={t`common.email`}
          control={control}
          name="email"
          rules={{ required: t`components.request-reset-form.email-required` }}
          placeholder={t`common.email`}
        />
        <SubmitCancelButtons
          submitButtonLabel={t`common.send`}
          disableSubmit={!isDirty}
        />
      </Form>
    </S.FormWrapper>
  );
};

type SubmitResetFormProps = {
  id: Uuid;
  token: string;
};

/**
 * Form for resetting password.
 */
const SubmitResetForm = ({ id, token }: SubmitResetFormProps): ReactElement => {
  const [_location, setLocation] = useLocation();
  const addToast = useAddToast();
  const passwordReset = usePasswordReset();
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<{ password: string; password1: string }>({});

  const onSubmit = handleSubmit(async (data) => {
    try {
      await passwordReset.mutateAsync({
        id,
        token,
        password: (data as { password: string }).password,
      });

      setLocation(routeUrls.public.login);

      addToast({
        kind: "success",
        title: t`common.password-change-success-toast-title`,
        timeout: 5000,
      });
    } catch {
      addToast({
        kind: "danger",
        title: t`common.error`,
        content: t`common.password-change-error-toast-content`,
        timeout: 5000,
      });
    }
  });

  return (
    <S.FormWrapper>
      <H5>{t`components.submit-reset-form.title`}</H5>
      <Form onSubmit={onSubmit}>
        <FormTextField
          control={control}
          name="password"
          rules={{ required: t`common.password-required` }}
          label={t`common.new-password-label`}
          type="new-password"
          autoComplete="new-password"
        />
        <FormTextField
          control={control}
          name="password1"
          label={t`common.confirm-new-password-label`}
          rules={{
            required: t`common.password-required`,
            validate: (value, formValues) => {
              return (
                value === formValues.password || t`common.password-mismatch`
              );
            },
          }}
          placeholder={t`common.password`}
          autoComplete="confirm-password"
          type="password"
        />
        <SubmitCancelButtons
          submitButtonLabel={t`common.change-password-submit`}
          disableSubmit={!isValid}
        />
      </Form>
    </S.FormWrapper>
  );
};

/**
 * Main account recovery view component.
 */
export const AccountRecovery = (): ReactElement => {
  const [location, setLocation] = useLocation();
  const [queryParams, setQueryParams] = useState<Partial<RecoveryParams>>({});
  const { data: user } = useCheckUser();

  useEffect(() => {
    // Redirect home if the user is already logged in.
    if (user) {
      setLocation(routeUrls.index);
    }

    const searchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(searchParams.entries());

    setQueryParams((p) => ({ ...p, ...params }));

    // Rewrite history to clear query params on pageload.
    setLocation(location, { replace: true });
  }, [location, user, setLocation, setQueryParams]);

  return !queryParams.i || !queryParams.t ? (
    <RequestResetForm />
  ) : (
    <SubmitResetForm id={queryParams.i} token={queryParams.t} />
  );
};
